import { useState } from "react";
import { Homeowner } from "./data";

interface HomeownerSelectionProps {
  homeowners: Homeowner[];
  loading?: boolean;
  onNext: (homeownerIds: string[]) => void;
  onBack: () => void;
}

export function HomeownerSelection({
  homeowners,
  loading = false,
  onNext,
  onBack,
}: HomeownerSelectionProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === homeowners.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(homeowners.map((h) => h.id));
    }
  };

  const handleNext = () => {
    if (selectedIds.length > 0) {
      onNext(selectedIds);
    }
  };

  const allSelected = homeowners.length > 0 && selectedIds.length === homeowners.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  return (
    <div className="">
      <div className="mb-8">
      <div className="mb-2 font-[500] text-[15px]">Select Homeowners</div>
      <div className="text-gray-600">
          Choose which homeowners to send contractor suggestions to
        </div>
      </div>

      <div className="mb-4 flex items-center justify-between border-b border-border pb-4">
        <div className="flex items-center gap-3">
          <input
            id="select-all-homeowners"
            type="checkbox"
            checked={allSelected}
            ref={(input) => {
              if (input) {
                input.indeterminate = someSelected;
              }
            }}
            onChange={toggleSelectAll}
            className="h-4 w-4 cursor-pointer rounded border-border"
          />
          <label className="cursor-pointer text-foreground" htmlFor="select-all-homeowners">
            Select All ({homeowners.length})
          </label>
        </div>

        <div className="text-muted-foreground">
          {selectedIds.length} selected
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-border">
        <table className="w-full">
          <thead className="border-b border-border bg-muted">
            <tr>
              <th className="px-4 py-3 text-left">
                <span className="sr-only">Select</span>
              </th>
              <th className="px-4 py-3 text-left text-muted-foreground">Name</th>
              <th className="px-4 py-3 text-left text-muted-foreground">Address</th>
              <th className="px-4 py-3 text-left text-muted-foreground">Contact</th>
            </tr>
          </thead>
          <tbody>
            {!loading && homeowners.length === 0 && (
              <tr className="bg-background">
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  No referral homeowners found for this agent.
                </td>
              </tr>
            )}
            {loading && (
              <tr className="bg-background">
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground" role="status">
                  Loading homeowners...
                </td>
              </tr>
            )}
            {homeowners.map((homeowner, index) => {
              const isSelected = selectedIds.includes(homeowner.id);
              return (
                <tr
                  key={homeowner.id}
                  className={`border-b border-border ${
                    isSelected ? "bg-primary/5" : "bg-background"
                  } ${index === homeowners.length - 1 ? "border-b-0" : ""}`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelection(homeowner.id)}
                      className="h-4 w-4 cursor-pointer rounded border-border"
                    />
                  </td>
                  <td className="px-4 py-3 text-foreground">{homeowner.name}</td>
                  <td className="px-4 py-3 text-foreground/80">
                    {homeowner.address || [homeowner.city, homeowner.state].filter(Boolean).join(", ") || "-"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-foreground/80">{homeowner.phone}</div>
                    <div className="text-muted-foreground">{homeowner.email}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex gap-3 mb-3">
        <button
          onClick={onBack}
          className="rounded-lg border border-border bg-background px-6 py-2 text-foreground hover:bg-accent"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={selectedIds.length === 0}
          className={`rounded-lg border-0 px-6 py-2 ${
            selectedIds.length === 0
              ? "cursor-not-allowed bg-muted text-muted-foreground"
              : "contractor-primary-action"
          }`}
        >
          Continue with {selectedIds.length} Selected
        </button>
      </div>
    </div>
  );
}
